import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaPlus, FaFileAlt, FaKey, FaTruck, FaRobot, FaSearch, FaSave, FaInfoCircle, FaEnvelope, FaPaperPlane, FaChevronDown, FaChevronRight, FaCheck } from 'react-icons/fa';
import getCarrierLogo from '../utils/carrierLogos';
import './CarriersInstructions.css';
import Button from './UI/Button';
import './UI/SearchInput.css';

// The select value of a carrier mail sender setting: '' for the platform,
// else the carrier page whose mailbox the carrier e-mails leave from.
const Carriers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSelectionOverlay, setShowSelectionOverlay] = useState(false);
  // Supported-carrier catalog (backend /carrier-catalog): the source of truth
  // for which carriers use web automation vs email.
  const [carrierCatalog, setCarrierCatalog] = useState([]);
  const [carrierRequests, setCarrierRequests] = useState([]);
  const [createError, setCreateError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  // "Request a carrier / custom integration" modal
  const [requestModal, setRequestModal] = useState(null); // {type, carrierName, nameLocked, carrierId}
  const [requestDetails, setRequestDetails] = useState('');
  const [requestCarrierName, setRequestCarrierName] = useState('');
  const [requestStatus, setRequestStatus] = useState(null); // null | 'sending' | 'sent' | 'duplicate' | 'error'
  const emptyNewCarrier = {
    name: '',
    emails: [],
    contact_rules: '',
    message_instructions: '',
    automation: false,
    is_mail: false,
    cc_emails: [],
    credentials: {
      username: '',
      password: ''
    },
    isAutomated: false,
    fromCatalog: false,
    requiresCredentials: false
  };
  const [newCarrier, setNewCarrier] = useState(emptyNewCarrier);

  // General carrier message instructions (user-level, applied to every carrier).
  const [messageConfig, setMessageConfig] = useState(null);
  const [generalInstructions, setGeneralInstructions] = useState('');
  const [messageConfigSaving, setMessageConfigSaving] = useState(false);
  const [messageConfigSaved, setMessageConfigSaved] = useState(false);
  const [messageConfigError, setMessageConfigError] = useState(null);
  const [showMessageConfig, setShowMessageConfig] = useState(false);

  // Where the agent's NEW carrier e-mail threads leave from (read-only,
  // /api/logistics/carrier-mail-sender): the account's Gmail mailbox
  // connected in Paramètres, else the platform's.
  const [mailSender, setMailSender] = useState(null);

  // List state (page, search, attention filter) lives in the URL so browser
  // back restores it after visiting a carrier (frontend-design.md).
  const [searchParams, setSearchParams] = useSearchParams();
  const PAGE_SIZE = 25;
  const searchQuery = searchParams.get('q') || '';
  const attentionFlag = searchParams.get('flag') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateListParams = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  };

  const handleSearchChange = (value) => updateListParams({ q: value, page: null }, { replace: true });
  const handlePageChange = (page) => updateListParams({ page: page <= 1 ? null : page });
  const toggleAttentionFlag = (flag) =>
    updateListParams({ flag: attentionFlag === flag ? null : flag, page: null });

  const carrierHasCredentials = (carrier) =>
    Boolean(carrier.credentials?.username || carrier.account_log);

  // Filter → sort → paginate, all client-side (the API returns the full list)
  const filteredCarriers = carriers
    .filter(carrier => carrier.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(carrier => {
      if (attentionFlag === 'missing_rules') return !carrier.contact_rules;
      if (attentionFlag === 'missing_credentials') return carrier.automation && !carrierHasCredentials(carrier);
      return true;
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));

  const totalPages = Math.max(1, Math.ceil(filteredCarriers.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const paginatedCarriers = filteredCarriers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const carrierStats = {
    total: carriers.length,
    automated: carriers.filter(carrier => carrier.automation).length,
    missingRules: carriers.filter(carrier => !carrier.contact_rules).length,
    missingCredentials: carriers.filter(carrier => carrier.automation && !carrierHasCredentials(carrier)).length
  };

  const mailSenderIsAccount = mailSender?.source === 'account_mailbox';

  useEffect(() => {
    fetchCarriers();
    fetchMessageConfig();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get('/api/logistics/carrier-mail-sender', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => setMailSender(response.data))
      .catch((error) => {
        console.error('Failed to fetch the carrier mail sender:', error?.response?.status || error.message);
      });
  }, []);

  const fetchMessageConfig = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carrier-message-config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessageConfig(response.data);
      setGeneralInstructions(response.data.general_instructions || '');
    } catch (error) {
      console.error('Failed to fetch carrier message config:', error);
    }
  };

  const handleSaveMessageConfig = async () => {
    setMessageConfigSaving(true);
    setMessageConfigError(null);
    setMessageConfigSaved(false);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        '/api/logistics/carrier-message-config',
        { general_instructions: generalInstructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageConfig((prev) => ({ ...prev, ...response.data }));
      setGeneralInstructions(response.data.general_instructions || '');
      setMessageConfigSaved(true);
    } catch (error) {
      setMessageConfigError(error.response?.data?.detail || error.message || 'Failed to save');
    } finally {
      setMessageConfigSaving(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data);
    } catch (error) {
      console.error('Failed to fetch carriers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarrierCatalog = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carrier-catalog', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarrierCatalog(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carrier catalog:', error);
      setCarrierCatalog([]);
    }
  };

  const fetchCarrierRequests = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carrier-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarrierRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch carrier requests:', error);
    }
  };

  const handleCreateCarrier = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: newCarrier.name,
        emails: newCarrier.emails.filter((email) => email && email.trim()),
        contact_rules: newCarrier.contact_rules,
        message_instructions: newCarrier.message_instructions || null,
        automation: newCarrier.automation,
        is_mail: newCarrier.is_mail,
        cc_emails: newCarrier.cc_emails || [],
      };
      const response = await axios.post('/api/logistics/carriers', payload, { headers });

      // Portal credentials go through the dedicated credentials API — the
      // create endpoint ignores a `credentials` field silently.
      const { username, password } = newCarrier.credentials || {};
      if (newCarrier.requiresCredentials && username && password && response.data?.id) {
        try {
          await axios.post('/api/logistics/carrier-credentials', {
            carrier_id: response.data.id,
            username,
            password,
            label: null,
            client_ids: [],
            additional_fields: {}
          }, { headers });
        } catch (credError) {
          console.error('Carrier created but saving credentials failed:', credError);
        }
      }

      setShowCreateModal(false);
      setNewCarrier(emptyNewCarrier);
      fetchCarriers();
    } catch (error) {
      setCreateError(error.response?.data?.detail || error.message || 'Failed to create carrier');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCarrierClick = (carrier) => {
    navigate(`/carriers-instructions/${carrier.id}`);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowSelectionOverlay(false);
    setCreateError(null);
  };

  const handleCreateButtonClick = async () => {
    setShowSelectionOverlay(true);
    await Promise.all([fetchCarrierCatalog(), fetchCarrierRequests()]);
  };

  const tenantCarrierNames = new Set(carriers.map((c) => (c.name || '').trim().toLowerCase()));
  const isCatalogCarrierAdded = (entry) => tenantCarrierNames.has(entry.name.trim().toLowerCase());
  const pendingRequests = carrierRequests.filter((r) => r.status === 'pending');

  const handleSelectCatalogCarrier = (entry) => {
    if (isCatalogCarrierAdded(entry)) return;
    const isWeb = entry.integration_mode === 'web_automation';
    setNewCarrier({
      ...emptyNewCarrier,
      name: entry.name,
      // Email carriers start with one empty address field ready to fill.
      emails: isWeb ? [] : [''],
      is_mail: !isWeb,
      isAutomated: isWeb,
      fromCatalog: true,
      requiresCredentials: Boolean(entry.requires_credentials)
    });
    setCreateError(null);
    setShowSelectionOverlay(false);
    setShowCreateModal(true);
  };

  const handleSelectManualCreation = () => {
    setNewCarrier({ ...emptyNewCarrier, is_mail: true, emails: [''] });
    setCreateError(null);
    setShowSelectionOverlay(false);
    setShowCreateModal(true);
  };

  const openRequestModal = ({ type, carrierName = '', nameLocked = false, carrierId = null }) => {
    setRequestModal({ type, nameLocked, carrierId });
    setRequestCarrierName(carrierName);
    setRequestDetails('');
    setRequestStatus(null);
  };

  const handleSubmitCarrierRequest = async () => {
    if (!requestCarrierName.trim()) return;
    setRequestStatus('sending');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/logistics/carrier-requests', {
        carrier_name: requestCarrierName.trim(),
        request_type: requestModal.type,
        details: requestDetails.trim() || null,
        carrier_id: requestModal.carrierId
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRequestStatus('sent');
      fetchCarrierRequests();
    } catch (error) {
      setRequestStatus(error.response?.status === 409 ? 'duplicate' : 'error');
    }
  };

  const handleAddEmail = () => {
    setNewCarrier({
      ...newCarrier,
      emails: [...newCarrier.emails, '']
    });
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...newCarrier.emails];
    updatedEmails[index] = value;
    setNewCarrier({
      ...newCarrier,
      emails: updatedEmails
    });
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = [...newCarrier.emails];
    updatedEmails.splice(index, 1);
    setNewCarrier({
      ...newCarrier,
      emails: updatedEmails
    });
  };

  // Numbered pager: first, last, and a window around the current page
  const getPageItems = () => {
    const anchors = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const pages = [...anchors].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const items = [];
    let prev = 0;
    pages.forEach(p => {
      if (p - prev > 1) items.push(`gap-${p}`);
      items.push(p);
      prev = p;
    });
    return items;
  };

  const renderPager = () => (
    <nav className="carriers-pager" aria-label={t('clientsList.pagination', 'Pagination')}>
      <button
        type="button"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        aria-label={t('common.previous')}
      >
        ‹
      </button>
      {getPageItems().map(item => (
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            className={item === currentPage ? 'active' : ''}
            aria-current={item === currentPage ? 'page' : undefined}
            onClick={() => handlePageChange(item)}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="pager-ellipsis" aria-hidden="true">…</span>
        )
      ))}
      <button
        type="button"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
        aria-label={t('common.next')}
      >
        ›
      </button>
    </nav>
  );

  if (isLoading) {
    return (
      <div className="carriers-instructions-container">
        <div className="carriers-card">
          <div className="carriers-skeleton" aria-label={t('carriers.loadingTitle', 'Chargement des transporteurs')} aria-busy="true">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="carriers-skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="carriers-instructions-container">
      <header className="carriers-page-head">
        <h1>{t('carriersInstructions.pageTitle', 'Transporteurs')}</h1>
        <p>{t('carriersInstructions.pageSubtitle', 'Les transporteurs de vos colis : adresses de contact, comptes portail et règles de réclamation. Relances, page de suivi et surveillance des colis sont dans le même menu.')}</p>
      </header>

      {/* General message instructions — collapsible config panel */}
      <div className="carriers-config-panel">
        <button
          type="button"
          className="carriers-config-header"
          onClick={() => setShowMessageConfig((v) => !v)}
          aria-expanded={showMessageConfig}
        >
          <div className="carriers-config-title">
            <FaFileAlt className="carriers-config-icon" />
            <div>
              <h3>{t('carrierMessageConfig.title', 'Instructions générales de message')}</h3>
              <p>
                {t('carrierMessageConfig.subtitle',
                  'Appliquées à chaque message envoyé à un transporteur. Des instructions par transporteur peuvent être définies sur sa page.')}
              </p>
            </div>
          </div>
          <div className="carriers-config-meta">
            <span className={`carriers-config-badge ${(messageConfig?.general_instructions || '').trim() ? 'configured' : ''}`}>
              {(messageConfig?.general_instructions || '').trim()
                ? t('souffranceSettings.configured', 'Configuré')
                : t('souffranceSettings.notConfigured', 'Défaut')}
            </span>
            {showMessageConfig ? <FaChevronDown /> : <FaChevronRight />}
          </div>
        </button>
        {showMessageConfig && (
          <div className="carriers-config-body">
            <textarea
              value={generalInstructions}
              onChange={(e) => {
                setGeneralInstructions(e.target.value);
                setMessageConfigSaved(false);
              }}
              rows={3}
              maxLength={messageConfig?.max_instructions_length || 2000}
              placeholder={
                messageConfig?.default_general_instructions
                  ? t('carrierMessageConfig.placeholder', 'Défaut : {{defaultInstructions}}', {
                      defaultInstructions: messageConfig.default_general_instructions,
                    })
                  : t('carrierMessageConfig.placeholderGeneric', 'Instructions générales pour les messages transporteur')
              }
              className="carriers-config-textarea"
            />
            <div className="carriers-config-footer">
              <span className="carriers-config-note">
                <FaInfoCircle />
                {t('carrierMessageConfig.hardConstraintsNote',
                  'Le numéro de suivi est toujours inclus automatiquement, et les messages Mondial Relay restent toujours sous {{limit}} caractères — quelles que soient ces instructions.',
                  { limit: messageConfig?.hard_limits?.['Mondial Relay'] || 255 })}
              </span>
              <div className="carriers-config-actions">
                {messageConfigError && (
                  <span className="carriers-config-error">{messageConfigError}</span>
                )}
                {messageConfigSaved && !messageConfigError && (
                  <span className="carriers-config-saved">{t('carrierMessageConfig.saved', 'Enregistré')}</span>
                )}
                <Button
                  variant="primary"
                  onClick={handleSaveMessageConfig}
                  disabled={messageConfigSaving || (messageConfig && (messageConfig.general_instructions || '') === generalInstructions.trim())}
                >
                  <FaSave /> {messageConfigSaving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Where the agent's carrier e-mails leave from — read-only, set in Paramètres */}
      <div className="carriers-config-panel">
        <button
          type="button"
          className="carriers-config-header"
          onClick={() => navigate('/parameters?tab=integrations')}
        >
          <div className="carriers-config-title">
            <FaEnvelope className="carriers-config-icon" />
            <div>
              <h3>{t('carrierMailSender.title', 'Boîte d’envoi des e-mails transporteur')}</h3>
              <p>
                {mailSenderIsAccount
                  ? t('carrierMailSender.accountMailbox',
                    'Les nouveaux échanges partent de la boîte Gmail du compte, où leurs réponses sont lues. Les échanges déjà ouverts gardent leur boîte. Modifier dans Paramètres.')
                  : t('carrierMailSender.platformMailbox',
                    'Les e-mails partent de la boîte de la plateforme. Connectez la boîte Gmail du compte dans Paramètres pour écrire avec votre adresse.')}
              </p>
              {mailSenderIsAccount && mailSender?.problem && (
                <span className="carriers-config-error">{mailSender.problem}</span>
              )}
            </div>
          </div>
          <div className="carriers-config-meta">
            <span className={`carriers-config-badge ${mailSenderIsAccount ? 'configured' : ''}`}>
              {mailSenderIsAccount ? mailSender.email : t('carrierMailSender.platformBadge', 'Plateforme')}
            </span>
            <FaChevronRight />
          </div>
        </button>
      </div>

      {/* Stats strip — flat tiles; the two warning tiles filter the list */}
      <div className="carriers-stats-strip" role="group" aria-label={t('carriersInstructions.title', 'Transporteurs')}>
        <div className="carrier-stat-tile">
          <span className="carrier-stat-label"><FaTruck /> {t('carriersInstructions.totalCarriers', 'Transporteurs')}</span>
          <span className="carrier-stat-value">{carrierStats.total}</span>
        </div>
        <div className="carrier-stat-tile">
          <span className="carrier-stat-label"><FaRobot /> {t('carriersInstructions.automated', 'Automatisés')}</span>
          <span className="carrier-stat-value">{carrierStats.automated}</span>
        </div>
        <button
          type="button"
          className={`carrier-stat-tile carrier-stat-tile--clickable${attentionFlag === 'missing_rules' ? ' active' : ''}`}
          onClick={() => toggleAttentionFlag('missing_rules')}
          aria-pressed={attentionFlag === 'missing_rules'}
        >
          <span className="carrier-stat-label warn"><FaFileAlt /> {t('carriersInstructions.missingRules', 'Règles manquantes')}</span>
          <span className="carrier-stat-value">{carrierStats.missingRules}</span>
        </button>
        <button
          type="button"
          className={`carrier-stat-tile carrier-stat-tile--clickable${attentionFlag === 'missing_credentials' ? ' active' : ''}`}
          onClick={() => toggleAttentionFlag('missing_credentials')}
          aria-pressed={attentionFlag === 'missing_credentials'}
        >
          <span className="carrier-stat-label warn"><FaKey /> {t('carriersInstructions.missingCredentials', 'Identifiants manquants')}</span>
          <span className="carrier-stat-value">{carrierStats.missingCredentials}</span>
        </button>
      </div>

      {/* Carriers card — header (search + count + create) / toolbar / table */}
      <div className="carriers-card">
        <div className="carriers-card-header">
          <div className="carriers-search">
            <FaSearch className="carriers-search-icon" aria-hidden="true" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('common.search')}
            />
            {searchQuery && (
              <button
                type="button"
                className="carriers-search-clear"
                onClick={() => handleSearchChange('')}
                aria-label={t('clearSearch', 'Effacer la recherche')}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <span className="carriers-count">
            {filteredCarriers.length === 1
              ? t('carriersList.countOne', '1 transporteur')
              : t('carriersList.countMany', '{{total}} transporteurs', { total: filteredCarriers.length })}
            {attentionFlag && (
              <button type="button" className="carriers-flag-clear" onClick={() => toggleAttentionFlag(attentionFlag)}>
                <FaTimes /> {attentionFlag === 'missing_rules'
                  ? t('carriersInstructions.missingRules', 'Règles manquantes')
                  : t('carriersInstructions.missingCredentials', 'Identifiants manquants')}
              </button>
            )}
          </span>
          <button
            type="button"
            className="u-btn u-btn--primary"
            data-tour="carriers-add"
            onClick={handleCreateButtonClick}
          >
            <FaPlus /> {t('carriersInstructions.createNew')}
          </button>
        </div>

        {totalPages > 1 && (
          <div className="carriers-toolbar">
            {renderPager()}
          </div>
        )}

        {paginatedCarriers.length > 0 ? (
          <div className="carriers-table-scroll">
            <table className="carriers-table">
              <colgroup>
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('carriersInstructions.name')}</th>
                  <th>{t('carriersList.mode', 'Mode')}</th>
                  <th>{t('carriersInstructions.emails')}</th>
                  <th>{t('carriersList.rules', 'Règles')}</th>
                  <th>{t('carriersInstructions.credentials', 'Identifiants')}</th>
                  <th>{t('common.automation', 'Automatisation')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {paginatedCarriers.map(carrier => {
                  const emails = carrier.emails || [];
                  const hasRules = Boolean(carrier.contact_rules);
                  const hasCredentials = carrierHasCredentials(carrier);
                  return (
                    <tr
                      key={carrier.id}
                      onClick={() => handleCarrierClick(carrier)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCarrierClick(carrier);
                        }
                      }}
                    >
                      <td className="cell-carrier" title={carrier.name}>
                        {getCarrierLogo(carrier.name) ? (
                          <span className="carrier-logo-chip" aria-hidden="true">
                            <img src={getCarrierLogo(carrier.name)} alt="" loading="lazy" />
                          </span>
                        ) : (
                          <span className="carrier-logo-chip carrier-logo-chip--fallback" aria-hidden="true">
                            <FaTruck className="carrier-row-icon" />
                          </span>
                        )}
                        <span className="carrier-row-name">{carrier.name}</span>
                      </td>
                      <td>
                        <span className={`carrier-mode-chip ${carrier.is_mail ? 'mail' : 'web'}`}>
                          {carrier.is_mail
                            ? <><FaEnvelope aria-hidden="true" /> {t('carriersInstructions.modeMail', 'Email')}</>
                            : <><FaRobot aria-hidden="true" /> {t('carriersList.modePortal', 'Portail')}</>}
                        </span>
                      </td>
                      <td className="cell-emails" title={emails.join(', ')}>
                        {emails.length > 0 ? (
                          <>
                            <span className="carrier-primary-email">{emails[0]}</span>
                            {emails.length > 1 && (
                              <span className="carrier-email-more">+{emails.length - 1}</span>
                            )}
                          </>
                        ) : (
                          <span className="muted-dash">–</span>
                        )}
                      </td>
                      <td>
                        {hasRules ? (
                          <span className="flag-ok" title={t('carriersList.rulesSet', 'Règles renseignées')}>
                            <FaCheck aria-hidden="true" />
                          </span>
                        ) : (
                          <span className="flag-warn">
                            <span className="warn-dot" aria-hidden="true" />
                            {t('clientsList.missing', 'manquantes')}
                          </span>
                        )}
                      </td>
                      <td>
                        {!carrier.automation ? (
                          <span className="muted-dash" title={t('carriersList.credentialsNotNeeded', 'Non requis (pas d\'automatisation)')}>–</span>
                        ) : hasCredentials ? (
                          <span className="flag-ok" title={t('carriersList.credentialsSet', 'Identifiants renseignés')}>
                            <FaCheck aria-hidden="true" />
                          </span>
                        ) : (
                          <span className="flag-warn">
                            <span className="warn-dot" aria-hidden="true" />
                            {t('carriersList.missingMasc', 'manquants')}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`auto-state ${carrier.automation ? 'on' : 'off'}`}>
                          <span className="state-dot" aria-hidden="true" />
                          {carrier.automation ? t('common.on') : t('common.off')}
                        </span>
                      </td>
                      <td className="cell-chevron">
                        <FaChevronRight aria-hidden="true" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="carriers-empty-state">
            {searchQuery || attentionFlag ? t('common.noSearchResults') : t('carriersInstructions.noCarriers')}
          </div>
        )}
      </div>

      {/* Selection Overlay — catalog picker */}
      {showSelectionOverlay && (
        <div className="modalai" onClick={handleCloseModal}>
          <div
            className="modal-content carrier-modal carrier-modal--wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="carrier-modal-header">
              <h3>{t('carriersInstructions.addCarrier')}</h3>
              <button
                type="button"
                className="carrier-modal-close"
                onClick={handleCloseModal}
                aria-label={t('common.cancel')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="carrier-modal-body">
              <div className="carrier-catalog-section" data-tour="catalog-web">
                <div className="carrier-catalog-section-header">
                  <span className="carrier-catalog-section-icon web"><FaRobot /></span>
                  <div>
                    <h4>{t('carriersInstructions.webAutomationSection', 'Web automation carriers')}</h4>
                    <p>{t('carriersInstructions.webAutomationHint', 'Unanimity files and follows claims directly on the carrier’s portal. Portal credentials may be required.')}</p>
                  </div>
                </div>
                <div className="carrier-catalog-grid">
                  {carrierCatalog.filter((c) => c.integration_mode === 'web_automation').map((entry) => {
                    const added = isCatalogCarrierAdded(entry);
                    return (
                      <button
                        key={entry.name}
                        type="button"
                        className={`carrier-catalog-item ${added ? 'added' : ''}`}
                        onClick={() => handleSelectCatalogCarrier(entry)}
                        disabled={added}
                      >
                        <span className="carrier-catalog-name">{entry.name}</span>
                        <span className="carrier-catalog-tags">
                          <span className="carrier-mode-pill web">
                            <FaRobot /> {t('carriersInstructions.modeAutomation', 'Automation')}
                          </span>
                          {added && (
                            <span className="carrier-mode-pill added-pill">
                              {t('carriersInstructions.alreadyAdded', 'Added')}
                            </span>
                          )}
                        </span>
                        {entry.detail && (
                          <span className="carrier-catalog-detail">
                            {t(`carriersInstructions.detail_${entry.detail}`,
                              entry.detail === 'portal_claims' ? 'Claims filed on the carrier portal'
                                : entry.detail === 'form_claims' ? 'Claims filed via the carrier’s claim form'
                                : entry.detail === 'api_claims' ? 'Claims filed via the carrier’s API'
                                : 'First contact by email, follow-ups on the portal')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="carrier-catalog-section" data-tour="catalog-mail">
                <div className="carrier-catalog-section-header">
                  <span className="carrier-catalog-section-icon mail"><FaEnvelope /></span>
                  <div>
                    <h4>{t('carriersInstructions.mailSection', 'Email carriers')}</h4>
                    <p>{t('carriersInstructions.mailHint', 'Unanimity contacts these carriers by email, using the contact addresses you provide.')}</p>
                  </div>
                </div>
                <div className="carrier-catalog-grid">
                  {carrierCatalog.filter((c) => c.integration_mode === 'mail').map((entry) => {
                    const added = isCatalogCarrierAdded(entry);
                    return (
                      <button
                        key={entry.name}
                        type="button"
                        className={`carrier-catalog-item ${added ? 'added' : ''}`}
                        onClick={() => handleSelectCatalogCarrier(entry)}
                        disabled={added}
                      >
                        <span className="carrier-catalog-name">{entry.name}</span>
                        <span className="carrier-catalog-tags">
                          <span className="carrier-mode-pill mail">
                            <FaEnvelope /> {t('carriersInstructions.modeMail', 'Email')}
                          </span>
                          {added && (
                            <span className="carrier-mode-pill added-pill">
                              {t('carriersInstructions.alreadyAdded', 'Added')}
                            </span>
                          )}
                        </span>
                        {entry.detail === 'mail_create_portal_updates' && (
                          <span className="carrier-catalog-detail">
                            {t('carriersInstructions.detail_mail_create_portal_updates', 'First contact by email, follow-ups on the portal')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="carrier-catalog-section carrier-request-cta">
                <div className="carrier-catalog-section-header">
                  <span className="carrier-catalog-section-icon request"><FaPaperPlane /></span>
                  <div>
                    <h4>{t('carriersInstructions.missingCarrierTitle', 'Can’t find your carrier?')}</h4>
                    <p>{t('carriersInstructions.missingCarrierHint', 'Request it and our team will build the integration — or add it yourself as an email carrier right away.')}</p>
                  </div>
                </div>
                <div className="carrier-request-actions">
                  <Button
                    variant="primary"
                    onClick={() => openRequestModal({ type: 'new_carrier' })}
                  >
                    <FaPaperPlane /> {t('carriersInstructions.requestCarrier', 'Request a carrier')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleSelectManualCreation}
                  >
                    <FaPlus /> {t('carriersInstructions.addCustomCarrier')}
                  </Button>
                </div>
                {pendingRequests.length > 0 && (
                  <div className="carrier-pending-requests">
                    <span className="carrier-pending-requests-label">
                      {t('carriersInstructions.pendingRequests', 'Pending requests')}:
                    </span>
                    {pendingRequests.map((request) => (
                      <span key={request.id} className="carrier-mode-pill pending-pill">
                        {request.carrier_name}
                        {request.request_type === 'custom_integration'
                          ? ` · ${t('carriersInstructions.modeCustom', 'custom')}`
                          : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modalai" onClick={handleCloseModal}>
          <div
            className="modal-content carrier-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="carrier-modal-header">
              <h3>{t('carriersInstructions.createNew')}</h3>
              <button
                type="button"
                className="carrier-modal-close"
                onClick={handleCloseModal}
                aria-label={t('common.cancel')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="carrier-modal-body">
              {newCarrier.fromCatalog && (
                <div className={`carrier-mode-banner ${newCarrier.isAutomated ? 'web' : 'mail'}`}>
                  {newCarrier.isAutomated ? <FaRobot /> : <FaEnvelope />}
                  <span>
                    {newCarrier.isAutomated
                      ? t('carriersInstructions.integrationWebBanner',
                          'Web automation — Unanimity files claims directly on the {{name}} portal.', { name: newCarrier.name })
                      : t('carriersInstructions.integrationMailBanner',
                          'Email — Unanimity contacts {{name}} at the addresses you add below.', { name: newCarrier.name })}
                  </span>
                </div>
              )}

              <div className="carrier-form-group">
                <label htmlFor="new-carrier-name">{t('carriersInstructions.name')}</label>
                <input
                  id="new-carrier-name"
                  type="text"
                  value={newCarrier.name}
                  onChange={(e) => setNewCarrier({
                    ...newCarrier,
                    name: e.target.value
                  })}
                  className="ai-agent-input"
                  readOnly={newCarrier.fromCatalog}
                  title={newCarrier.fromCatalog
                    ? t('carriersInstructions.nameLockedHint', 'Catalog carrier names are fixed so automation routing works reliably.')
                    : undefined}
                />
              </div>

              {/* Only show email fields for manual carriers */}
              {!newCarrier.isAutomated && (
                <div className="carrier-form-group" data-tour="carrier-emails">
                  <label>{t('carriersInstructions.emails')}</label>
                  {newCarrier.emails.map((email, index) => (
                    <div key={index} className="email-input-group">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                        placeholder={t('carriersInstructions.emailPlaceholder')}
                        className="ai-agent-input"
                      />
                      <button
                        type="button"
                        className="carrier-modal-remove-btn"
                        onClick={() => handleRemoveEmail(index)}
                        aria-label={t('common.delete', 'Supprimer')}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="carrier-modal-add-btn"
                    onClick={handleAddEmail}
                  >
                    <FaPlus /> {t('carriersInstructions.addEmail')}
                  </button>
                </div>
              )}

              <div className="carrier-form-group">
                <label htmlFor="new-carrier-rules">{t('carriersInstructions.contactRules')}</label>
                <textarea
                  id="new-carrier-rules"
                  value={newCarrier.contact_rules}
                  onChange={(e) => setNewCarrier({
                    ...newCarrier,
                    contact_rules: e.target.value
                  })}
                  rows={4}
                  className="ai-agent-textarea"
                />
              </div>
              <div className="carrier-form-group">
                <label htmlFor="new-carrier-message-instructions">{t('carriersInstructions.messageInstructions', 'Instructions de message (optionnel)')}</label>
                <textarea
                  id="new-carrier-message-instructions"
                  value={newCarrier.message_instructions}
                  onChange={(e) => setNewCarrier({
                    ...newCarrier,
                    message_instructions: e.target.value
                  })}
                  rows={3}
                  className="ai-agent-textarea"
                />
              </div>

              <div className="carrier-form-group" data-tour="carrier-automation">
                <label className="carrier-inline-toggle">
                  <input
                    type="checkbox"
                    checked={newCarrier.automation}
                    onChange={(e) => setNewCarrier({
                      ...newCarrier,
                      automation: e.target.checked
                    })}
                  />
                  {t('carriersList.automationEnabled', 'Automatisation activée pour ce transporteur')}
                </label>
              </div>
              {/* The integration mode is fixed for catalog carriers */}
              {!newCarrier.fromCatalog && (
                <div className="carrier-form-group">
                  <label className="carrier-inline-toggle">
                    <input
                      type="checkbox"
                      checked={newCarrier.is_mail}
                      onChange={(e) => setNewCarrier({
                        ...newCarrier,
                        is_mail: e.target.checked
                      })}
                    />
                    {t('carriersList.isMailLabel', 'Transporteur contacté par e-mail')}
                  </label>
                </div>
              )}

              {/* Portal credentials — only for automation carriers that need a login */}
              {newCarrier.requiresCredentials && (
                <div className="carrier-form-group" data-tour="carrier-credentials">
                  <label>{t('carriersInstructions.portalCredentials', 'Portal credentials')}</label>
                  <p className="carrier-credentials-hint">
                    {t('carriersInstructions.credentialsHint', 'Used by the automation to sign in to the carrier portal. You can also add them later on the carrier page.')}
                  </p>
                  <input
                    type="text"
                    value={newCarrier.credentials.username}
                    onChange={(e) => setNewCarrier({
                      ...newCarrier,
                      credentials: {
                        ...newCarrier.credentials,
                        username: e.target.value
                      }
                    })}
                    placeholder={t('carriersInstructions.username')}
                    className="ai-agent-input"
                    autoComplete="off"
                  />
                  <input
                    type="password"
                    value={newCarrier.credentials.password}
                    onChange={(e) => setNewCarrier({
                      ...newCarrier,
                      credentials: {
                        ...newCarrier.credentials,
                        password: e.target.value
                      }
                    })}
                    placeholder={t('carriersInstructions.password')}
                    className="ai-agent-input"
                    autoComplete="new-password"
                  />
                </div>
              )}

              {newCarrier.fromCatalog && (
                <button
                  type="button"
                  className="carrier-custom-request-link"
                  onClick={() => {
                    setShowCreateModal(false);
                    openRequestModal({ type: 'custom_integration', carrierName: newCarrier.name, nameLocked: true });
                  }}
                >
                  {t('carriersInstructions.customIntegrationHint',
                    'Your process for {{name}} is different (dedicated forms, specific portal)? Request a custom integration.',
                    { name: newCarrier.name })}
                </button>
              )}

              {createError && (
                <div className="carrier-create-error">{createError}</div>
              )}
            </div>

            <div className="carrier-modal-footer">
              <button
                type="button"
                className="u-btn u-btn--secondary"
                onClick={handleCloseModal}
                disabled={isCreating}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="u-btn u-btn--primary"
                data-tour="carrier-create"
                onClick={handleCreateCarrier}
                disabled={isCreating || !newCarrier.name.trim()}
              >
                {isCreating ? t('common.saving', 'Enregistrement...') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request a carrier / custom integration */}
      {requestModal && (
        <div className="modalai" onClick={() => setRequestModal(null)}>
          <div
            className="modal-content carrier-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="carrier-modal-header">
              <h3>
                {requestModal.type === 'new_carrier'
                  ? t('carriersInstructions.requestModalTitleNew', 'Request a new carrier')
                  : t('carriersInstructions.requestModalTitleCustom', 'Request a custom integration')}
              </h3>
              <button
                type="button"
                className="carrier-modal-close"
                onClick={() => setRequestModal(null)}
                aria-label={t('common.cancel')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="carrier-modal-body">
              <p className="carrier-request-note">
                {requestModal.type === 'new_carrier'
                  ? t('carriersInstructions.requestNewNote', 'Tell us which carrier you work with — our team will look into integrating it.')
                  : t('carriersInstructions.requestCustomNote', 'Describe how your process differs from the standard integration (dedicated claim forms, a specific portal, a dedicated contact…).')}
              </p>
              <div className="carrier-form-group">
                <label htmlFor="request-carrier-name">{t('carriersInstructions.name')}</label>
                <input
                  id="request-carrier-name"
                  type="text"
                  value={requestCarrierName}
                  onChange={(e) => setRequestCarrierName(e.target.value)}
                  className="ai-agent-input"
                  readOnly={requestModal.nameLocked}
                  maxLength={100}
                />
              </div>
              <div className="carrier-form-group">
                <label htmlFor="request-carrier-details">{t('carriersInstructions.requestDetailsLabel', 'Détails')}</label>
                <textarea
                  id="request-carrier-details"
                  value={requestDetails}
                  onChange={(e) => setRequestDetails(e.target.value)}
                  placeholder={t('carriersInstructions.requestDetailsPlaceholder', 'Describe how you work with this carrier (portal, forms, contact addresses, volumes…)')}
                  className="ai-agent-textarea"
                  rows={4}
                  maxLength={2000}
                />
              </div>
              {requestStatus === 'sent' && (
                <div className="carrier-request-feedback success">
                  {t('carriersInstructions.requestSent', 'Request sent — our team will get back to you.')}
                </div>
              )}
              {requestStatus === 'duplicate' && (
                <div className="carrier-request-feedback warning">
                  {t('carriersInstructions.requestAlreadyPending', 'A request for this carrier is already pending.')}
                </div>
              )}
              {requestStatus === 'error' && (
                <div className="carrier-request-feedback error">
                  {t('carriersInstructions.requestFailed', 'Failed to send the request. Please try again.')}
                </div>
              )}
            </div>

            <div className="carrier-modal-footer">
              {requestStatus === 'sent' ? (
                <button type="button" className="u-btn u-btn--primary" onClick={() => setRequestModal(null)}>
                  {t('common.close', 'Fermer')}
                </button>
              ) : (
                <>
                  <button type="button" className="u-btn u-btn--secondary" onClick={() => setRequestModal(null)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="u-btn u-btn--primary"
                    onClick={handleSubmitCarrierRequest}
                    disabled={requestStatus === 'sending' || !requestCarrierName.trim()}
                  >
                    <FaPaperPlane /> {requestStatus === 'sending' ? t('common.saving', 'Enregistrement...') : t('carriersInstructions.sendRequest', 'Send request')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carriers;
